import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Polls /admin/reservations periodically. When a new reservation appears
 * (i.e. one we haven't seen since the hook mounted), it fires:
 *  - a short audible chime (Web Audio API — no assets)
 *  - a sonner toast
 *  - a browser Notification (if the user granted permission)
 *
 * Returns { newCount, resetNewCount }.
 */
export default function useReservationAlerts({ intervalMs = 15000, onNew } = {}) {
  const seenRef = useRef(new Set());
  const bootedRef = useRef(false);
  const [newCount, setNewCount] = useState(0);

  const playChime = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      // two short pings
      [0, 0.18].forEach((offset, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(i === 0 ? 880 : 1175, now + offset);
        gain.gain.setValueAtTime(0, now + offset);
        gain.gain.linearRampToValueAtTime(0.25, now + offset + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + offset);
        osc.stop(now + offset + 0.2);
      });
      setTimeout(() => ctx.close(), 800);
    } catch { /* ignore */ }
  };

  const notify = (r) => {
    const title = `Nueva reserva · Ficha #${r.ficha_number}`;
    const body = `${r.buyer_name || "Cliente"}${r.buyer_phone ? " · " + r.buyer_phone : ""}`;
    toast(title, { description: body, duration: 8000 });
    playChime();
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      try {
        new Notification(title, { body, tag: `reserva-${r.ficha_number}` });
      } catch { /* ignore */ }
    }
    if (onNew) onNew(r);
  };

  useEffect(() => {
    // Ask for browser notification permission (non-blocking)
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/reservations");
        const list = Array.isArray(data) ? data : [];
        if (!bootedRef.current) {
          // Baseline — mark everything already there as "seen" so we don't spam on first load.
          list.forEach((r) => seenRef.current.add(r.ficha_number));
          bootedRef.current = true;
          return;
        }
        const currentIds = new Set(list.map((r) => r.ficha_number));
        // Drop from seen those reservations that no longer exist (expired/confirmed/cancelled)
        for (const id of Array.from(seenRef.current)) {
          if (!currentIds.has(id)) seenRef.current.delete(id);
        }
        // Detect newcomers
        const newcomers = list.filter((r) => !seenRef.current.has(r.ficha_number));
        newcomers.forEach((r) => {
          seenRef.current.add(r.ficha_number);
          notify(r);
        });
        if (newcomers.length > 0) {
          setNewCount((c) => c + newcomers.length);
        }
      } catch { /* silent — polling shouldn't spam errors */ }
    };

    tick(); // immediate baseline
    const id = setInterval(() => { if (!stopped) tick(); }, intervalMs);
    return () => {
      stopped = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs]);

  return { newCount, resetNewCount: () => setNewCount(0) };
}
